import axiosClient from '../libs/axios';

// Not türleri
export const NOTE_PRIORITIES = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  URGENT: 4,
  CRITICAL: 5
};

// Not durumları
export const NOTE_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  ARCHIVED: 'archived'
};

// Priority'ye göre renk belirleme
export const getColorFromPriority = (priority) => {
  const priorityToColor = {
    1: 'green',        // 1 - Yeşil
    2: 'yellow',       // 2 - Sarı
    3: 'blue',         // 3 - Mavi
    4: 'orange',       // 4 - Turuncu
    5: 'red'           // 5 - Kırmızı
  };
  return priorityToColor[priority] || 'yellow';
};

// Unicode escape karakterlerini decode et (sadece notlar için)
const decodeUnicodeString = (str) => {
  if (!str || typeof str !== 'string') return str;
  
  try {
    // Unicode escape sequence'lerini decode et (\u015f -> ş)
    return str.replace(/\\u([0-9a-fA-F]{4})/g, (match, grp) => {
      return String.fromCharCode(parseInt(grp, 16));
    });
  } catch (error) {
    console.error('Unicode decode error:', error);
    return str;
  }
};

// API'den gelen notu frontend formatına çevir
export const formatNoteFromApi = (apiNote) => {

  
  // İçeriği ve başlığı decode et - Unicode karakterleri Türkçe'ye çevir
  const rawContent = apiNote.tcontent || apiNote.content || apiNote.note_content || '';
  const rawSubject = apiNote.tsubject || apiNote.subject || apiNote.note_subject || '';
  
  const decodedContent = decodeUnicodeString(rawContent);
  const decodedSubject = decodeUnicodeString(rawSubject);
  
  const formattedNote = {
    id: apiNote.noteid || apiNote.id || apiNote.note_id,
    subject: decodedSubject,
    content: decodedContent,
    priority: apiNote.tpriority || NOTE_PRIORITIES.MEDIUM,
    status: apiNote.tstatus || apiNote.status || apiNote.note_status || NOTE_STATUS.PENDING,
    createdAt: apiNote.created_at || apiNote.createdAt || apiNote.created_date,
    updatedAt: apiNote.updated_at || apiNote.updatedAt || apiNote.updated_date,
    completedAt: apiNote.completed_at || apiNote.completedAt || apiNote.completed_date,
    completedText: apiNote.notetext || apiNote.completedText || apiNote.completed_text,
    // Renk bilgisi - API'den gelen tcolor veya color alanını kullan, yoksa tpriority'ye göre belirle
    color: apiNote.tcolor || apiNote.color || getColorFromPriority(apiNote.tpriority || NOTE_PRIORITIES.MEDIUM)
  };
  
  
  return formattedNote;
};

// Frontend formatından API formatına çevir
export const formatNoteForApi = (note) => {
 
  // Priority değerini güvenli bir şekilde dönüştür
  const priority = typeof note.priority === 'number' ? note.priority : parseInt(note.priority) || NOTE_PRIORITIES.MEDIUM;
  
  const apiData = {
    tsubject: note.subject,
    tcontent: note.content || '', // İçeriği olduğu gibi gönder - Türkçe karakterleri korumak için
    tpriority: priority, // API'de tpriority kullanılıyor
    tcolor: note.color || 'yellow' // Renk bilgisini API'ye gönder
  };
  
 
  return apiData;
};

// Tek not detayını getir
export const getNote = async (noteId) => {
  try {
    const response = await axiosClient.post('/mynotes', {
      noteid: noteId
    });
    
    // API'den gelen veri yapısını kontrol et
    if (response.data && response.data.note) {
      return formatNoteFromApi(response.data.note);
    } else if (response.data) {
      return formatNoteFromApi(response.data);
    } else {
      throw new Error('Note not found');
    }
  } catch (error) {
    console.error('getNote error:', error);
    throw error;
  }
};

// Kullanıcı notlarını listele
export const getNotesList = async () => {
  try {
    const response = await axiosClient.get('/mynotes/list');
  
    // Eğer response.data bir array ise direkt map yap
    if (Array.isArray(response.data)) {
      return response.data.map(formatNoteFromApi);
    }
    
    // Eğer response.data bir object ise ve içinde usersNotesData array'i varsa
    if (response.data && Array.isArray(response.data.usersNotesData)) {
      return response.data.usersNotesData.map(formatNoteFromApi);
    }
    
    // Eğer response.data bir object ise ve içinde notes array'i varsa
    if (response.data && Array.isArray(response.data.notes)) {
      return response.data.notes.map(formatNoteFromApi);
    }
    
    // Eğer response.data bir object ise ve içinde data array'i varsa
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data.map(formatNoteFromApi);
    }
    
    // Eğer hiçbiri değilse boş array döndür
    console.warn('Unexpected API response structure:', response.data);
    return [];
  } catch (error) {
    console.error('getNotesList error:', error);
    throw error;
  }
};

// Yeni not oluştur
export const createNote = async (noteData) => {
  try {
    const formattedData = formatNoteForApi(noteData);
    const response = await axiosClient.post('/mynotes/set', formattedData);
    
    // API'den gelen veri yapısını kontrol et
    if (response.data && response.data.note) {
      return formatNoteFromApi(response.data.note);
    } else if (response.data && typeof response.data === 'object' && response.data.id) {
      return formatNoteFromApi(response.data);
    } else if (response.status === 200 || response.data === 200) {
      // API sadece status code döndürüyorsa başarılı olduğunu varsay
      return { success: true, message: 'Note created successfully' };
    } else {
      throw new Error('Unexpected response structure');
    }
  } catch (error) {
    console.error('createNote error:', error);
    throw error;
  }
};

// Notu güncelle
export const updateNote = async (noteId, noteData) => {
  try {
    
    const formattedData = formatNoteForApi(noteData);
    
    // Yeni API endpoint'ini kullan - /mynotes/update
    const updateData = {
      noteid: parseInt(noteId), // noteid'yi integer olarak gönder
      ...formattedData
    };
    
    
    const response = await axiosClient.post('/mynotes/update', updateData);
    
    // API'den gelen veri yapısını kontrol et
    if (response.data && response.data.note) {
      return formatNoteFromApi(response.data.note);
    } else if (response.data && typeof response.data === 'object' && response.data.id) {
      return formatNoteFromApi(response.data);
    } else if (response.status === 200 || response.data === 200) {
      // API sadece status code döndürüyorsa başarılı olduğunu varsay
      return { success: true, message: 'Note updated successfully' };
    } else {
      throw new Error('Unexpected response structure');
    }
  } catch (error) {
    console.error('updateNote error:', error);
    throw error;
  }
};

// Notu tamamla
export const completeNote = async (noteId, completedText = 'Tamamlandı') => {
  try {
    const response = await axiosClient.post('/mynotes/finish', {
      noteid: noteId,
      notetext: completedText
    });
    
    // API'den gelen veri yapısını kontrol et
    if (response.data && response.data.note) {
      return formatNoteFromApi(response.data.note);
    } else if (response.data && typeof response.data === 'object' && response.data.id) {
      return formatNoteFromApi(response.data);
    } else if (response.status === 200 || response.data === 200) {
      // API sadece status code döndürüyorsa başarılı olduğunu varsay
      return { success: true, message: 'Note completed successfully' };
    } else {
      return { success: true }; // Sadece başarı mesajı döndüyse
    }
  } catch (error) {
    console.error('completeNote error:', error);
    throw error;
  }
};

// Not sil
export const deleteNote = async (noteId) => {
  try {
    const response = await axiosClient.post('/mynotes/delete', {
      noteid: noteId
    });
    
    // API'den gelen veri yapısını kontrol et
    if (response.data && typeof response.data === 'object') {
      return response.data;
    } else if (response.status === 200 || response.data === 200) {
      // API sadece status code döndürüyorsa başarılı olduğunu varsay
      return { success: true, message: 'Note deleted successfully' };
    } else {
      return { success: true };
    }
  } catch (error) {
    console.error('deleteNote error:', error);
    throw error;
  }
};

// Toplu not silme
export const deleteMultipleNotes = async (noteIds) => {
  try {
    // Her notu sırayla sil
    const deletePromises = noteIds.map(noteId => deleteNote(noteId));
    await Promise.all(deletePromises);
    
    return { 
      success: true, 
      message: `${noteIds.length} not başarıyla silindi` 
    };
  } catch (error) {
    console.error('deleteMultipleNotes error:', error);
    throw error;
  }
};

// Öncelik etiketleri
export const getPriorityLabel = (priority) => {
  const labels = {
    [NOTE_PRIORITIES.LOW]: 'Düşük',
    [NOTE_PRIORITIES.MEDIUM]: 'Orta',
    [NOTE_PRIORITIES.HIGH]: 'Yüksek',
    [NOTE_PRIORITIES.URGENT]: 'Acil',
    [NOTE_PRIORITIES.CRITICAL]: 'Kritik'
  };
  return labels[priority] || 'Orta';
};

// Priority'ye göre kart rengi mapping
export const getPriorityCardColor = (priority) => {
  const colorMap = {
    1: 'green',    // Düşük - Yeşil
    2: 'yellow',   // Orta - Sarı
    3: 'blue',     // Yüksek - Mavi
    4: 'orange',   // Acil - Turuncu
    5: 'red'       // Kritik - Kırmızı
  };
  return colorMap[priority] || 'yellow';
};

// Öncelik renkleri (badge için)
export const getPriorityColor = (priority) => {
  const colors = {
    [NOTE_PRIORITIES.LOW]: 'text-green-600 bg-green-100',
    [NOTE_PRIORITIES.MEDIUM]: 'text-blue-600 bg-blue-100',
    [NOTE_PRIORITIES.HIGH]: 'text-orange-600 bg-orange-100',
    [NOTE_PRIORITIES.URGENT]: 'text-red-600 bg-red-100',
    [NOTE_PRIORITIES.CRITICAL]: 'text-purple-600 bg-purple-100'
  };
  return colors[priority] || 'text-blue-600 bg-blue-100';
};

// Durum etiketleri
export const getStatusLabel = (status) => {
  const labels = {
    [NOTE_STATUS.PENDING]: 'Beklemede',
    [NOTE_STATUS.COMPLETED]: 'Tamamlandı',
    [NOTE_STATUS.ARCHIVED]: 'Arşivlendi'
  };
  return labels[status] || 'Beklemede';
};

// Durum renkleri
export const getStatusColor = (status) => {
  const colors = {
    [NOTE_STATUS.PENDING]: 'text-yellow-600 bg-yellow-100',
    [NOTE_STATUS.COMPLETED]: 'text-green-600 bg-green-100',
    [NOTE_STATUS.ARCHIVED]: 'text-gray-600 bg-gray-100'
  };
  return colors[status] || 'text-yellow-600 bg-yellow-100';
};

// Notları önceliğe göre sırala
export const sortNotesByPriority = (notes) => {
  return notes.sort((a, b) => b.priority - a.priority);
};

// Notları duruma göre filtrele
export const filterNotesByStatus = (notes, status) => {
  return notes.filter(note => note.status === status);
};

// Notları önceliğe göre filtrele
export const filterNotesByPriority = (notes, priority) => {
  return notes.filter(note => note.priority === priority);
};

// Notları arama
export const searchNotes = (notes, searchTerm) => {
  if (!searchTerm) return notes;
  
  const term = searchTerm.toLowerCase();
  return notes.filter(note => 
    note.subject.toLowerCase().includes(term) ||
    note.content.toLowerCase().includes(term)
  );
};
